import { normalizeUrl } from "@/lib/utils";

export const DISCOVER_TRAFFIC_SOURCE_PARAM = "traffic_source";
export const DISCOVER_TRAFFIC_SOURCE_COOKIE = "discover_traffic_source";
export const DISCOVER_TRAFFIC_SOURCE_STORAGE_KEY = "discover-traffic-source";
export const SPOND_CLUB_TRAFFIC_SOURCE = "spond_club";

export type DiscoverTrafficSource = typeof SPOND_CLUB_TRAFFIC_SOURCE;

export type DiscoverSessionProps = ReturnType<typeof getDiscoverSessionProps>;

type SearchParamReader = {
  get: (name: string) => string | null;
};

type DiscoverAttributionOptions = {
  embeddedInApp?: boolean;
};

export function getDiscoverAttribution(
  trafficSource: string | null | undefined,
  { embeddedInApp = false }: DiscoverAttributionOptions = {},
) {
  if (embeddedInApp) {
    return {
      discover_origin: "spond_app",
      discover_origin_category: "spond_app",
      is_organic_discover: false,
      registration_source: "discover",
    } as const;
  }

  if (!isKnownDiscoverTrafficSource(trafficSource)) {
    return {
      discover_origin: "organic_discover",
      discover_origin_category: "organic",
      is_organic_discover: true,
      registration_source: "discover",
    } as const;
  }

  return {
    discover_origin: "spond_club",
    discover_origin_category: "spond_club",
    is_organic_discover: false,
    registration_source: "direct",
  } as const;
}

export function getDiscoverAnalyticsProps(
  trafficSource: string | null | undefined,
  options?: DiscoverAttributionOptions,
) {
  const { registration_source: _registrationSource, ...analyticsProps } =
    getDiscoverAttribution(trafficSource, options);
  return analyticsProps;
}

/**
 * The props a Discover session event carries: the attribution props, the
 * `signup_surface` and the browser referrer. Call it in the browser, from the
 * event's callback.
 */
export function getDiscoverSessionProps(
  searchParams: SearchParamReader | null | undefined,
  embeddedInApp: boolean,
) {
  return {
    ...getDiscoverAnalyticsProps(
      resolveCurrentDiscoverTrafficSource(searchParams),
      { embeddedInApp },
    ),
    signup_surface: embeddedInApp ? "in_app" : "web",
    referrer: document.referrer || null,
  } as const;
}

export function getTrafficSourceFromSearchParams(
  searchParams: SearchParamReader | null | undefined,
): DiscoverTrafficSource | null {
  return normalizeDiscoverTrafficSource(getRawTrafficSource(searchParams));
}

export function isKnownDiscoverTrafficSource(
  trafficSource: string | null | undefined,
): trafficSource is DiscoverTrafficSource {
  return trafficSource === SPOND_CLUB_TRAFFIC_SOURCE;
}

export function normalizeDiscoverTrafficSource(
  trafficSource: string | null | undefined,
): DiscoverTrafficSource | null {
  return isKnownDiscoverTrafficSource(trafficSource) ? trafficSource : null;
}

export function persistDiscoverTrafficSource(
  searchParams: SearchParamReader | null | undefined,
): void {
  const trafficSource = getTrafficSourceFromSearchParams(searchParams);
  if (!trafficSource || typeof window === "undefined") return;

  try {
    window.sessionStorage.setItem(
      DISCOVER_TRAFFIC_SOURCE_STORAGE_KEY,
      trafficSource,
    );
  } catch {
    // Ignore storage failures; URL attribution still works for this page.
  }
}

export function getStoredDiscoverTrafficSource(): string | null {
  if (typeof window === "undefined") return null;

  try {
    return (
      window.sessionStorage.getItem(DISCOVER_TRAFFIC_SOURCE_STORAGE_KEY) || null
    );
  } catch {
    return null;
  }
}

/**
 * Read the attribution cookie middleware sets when consent is accepted. Kept
 * non-httpOnly so a new tab on a cached detail URL can still recover
 * `spond_club` without the original query string or sessionStorage.
 */
export function getCookieDiscoverTrafficSource(): string | null {
  if (typeof document === "undefined") return null;

  const match = document.cookie.match(
    new RegExp(`(?:^|; *)${DISCOVER_TRAFFIC_SOURCE_COOKIE}=([^;]*)`),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

export function resolveCurrentDiscoverTrafficSource(
  searchParams: SearchParamReader | null | undefined,
): DiscoverTrafficSource | null {
  const rawTrafficSource = getRawTrafficSource(searchParams);
  if (rawTrafficSource !== null) {
    return normalizeDiscoverTrafficSource(rawTrafficSource);
  }

  return (
    normalizeDiscoverTrafficSource(getStoredDiscoverTrafficSource()) ??
    normalizeDiscoverTrafficSource(getCookieDiscoverTrafficSource())
  );
}

function getRawTrafficSource(
  searchParams: SearchParamReader | null | undefined,
): string | null {
  return searchParams?.get(DISCOVER_TRAFFIC_SOURCE_PARAM) || null;
}

export function addRegistrationSourceParam(
  url: string,
  trafficSource: string | null | undefined,
): string {
  const { registration_source: registrationSource } =
    getDiscoverAttribution(trafficSource);

  const normalized = normalizeUrl(url);
  if (!normalized) return "";

  const urlObj = new URL(normalized);
  urlObj.searchParams.set("source", registrationSource);
  return urlObj.toString();
}
